import { useMotionValueEvent } from "motion/react";
import {
  FC,
  PointerEvent as ReactPointerEvent,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { RideInvestigationSeverityEnum } from "@ryde/shared";
import { greyMui } from "@ryde/theme";

import {
  PERCENT,
  RIDE_INVESTIGATION_SEVERITY_COLOR,
} from "../investigation.constants";
import { ITimelineTick } from "../investigation.models";
import { fractionForTime } from "../investigation.utils";
import { usePlaybackContext } from "../playback/ride-investigation-playback.context";
import { usePlaybackSnapshot } from "../playback/use-playback-snapshot";
import {
  TimelineFillStyled,
  TimelinePlayheadStyled,
  TimelineTickStyled,
  TimelineTrackStyled,
} from "./investigation.transport.styled";

/**
 * Scrubbable timeline. Station arrivals show as thin neutral ticks; warning/critical events (gaps,
 * detours, stops, no-shows, late arrivals) show as colored dots. The current station and the most
 * recently fired event are highlighted (enlarged + glow), mirroring the station list and event feed.
 * The playhead and progress fill are updated imperatively from the motion value.
 */
export const InvestigationTimeline: FC = () => {
  const { model, playheadMs, seekToFraction, pause, justFiredEvent } =
    usePlaybackContext();
  const snapshot = usePlaybackSnapshot();
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const fillRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  const currentStationIndex = snapshot?.currentStationIndex ?? -1;
  const activeEventId = justFiredEvent?.event.id;

  const ticks = useMemo<ITimelineTick[]>(() => {
    if (!model) {
      return [];
    }

    const stationTicks: ITimelineTick[] = model.stations
      .map((entry, index) => ({ entry, index }))
      .filter(({ entry }) => entry.tMs !== undefined)
      .map(({ entry, index }) => ({
        key: `station-${entry.station.id}`,
        fraction: fractionForTime(model, entry.tMs as number),
        color: greyMui._500,
        isStation: true,
        title: entry.station.name,
        stationIndex: index,
      }));

    const eventTicks: ITimelineTick[] = model.events
      .filter(
        entry => entry.event.severity !== RideInvestigationSeverityEnum.Info,
      )
      .map(entry => ({
        key: `event-${entry.event.id}`,
        fraction: fractionForTime(model, entry.tMs),
        color: RIDE_INVESTIGATION_SEVERITY_COLOR[entry.event.severity],
        isStation: false,
        title: t(entry.event.labelKey),
        eventId: entry.event.id,
      }));

    return [...stationTicks, ...eventTicks];
  }, [model, t]);

  const applyFraction = useEvent((fraction: number) => {
    const percent = `${fraction * PERCENT}%`;

    if (thumbRef.current) {
      thumbRef.current.style.left = percent;
    }

    if (fillRef.current) {
      fillRef.current.style.width = percent;
    }
  });

  useEffect(() => {
    if (model) {
      applyFraction(fractionForTime(model, playheadMs.get()));
    }
  }, [model, playheadMs, applyFraction]);

  useMotionValueEvent(playheadMs, "change", (ms: number) => {
    if (model) {
      applyFraction(fractionForTime(model, ms));
    }
  });

  const seekFromClientX = useEvent((clientX: number) => {
    const element = trackRef.current;

    if (!element) {
      return;
    }

    const rect = element.getBoundingClientRect();
    const fraction = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
    seekToFraction(fraction);
  });

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>): void => {
    draggingRef.current = true;
    pause();
    trackRef.current?.setPointerCapture(event.pointerId);
    seekFromClientX(event.clientX);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (draggingRef.current) {
      seekFromClientX(event.clientX);
    }
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>): void => {
    draggingRef.current = false;
    trackRef.current?.releasePointerCapture(event.pointerId);
  };

  if (!model) {
    return null;
  }

  return (
    <TimelineTrackStyled
      ref={trackRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <TimelineFillStyled ref={fillRef} />
      {ticks.map(tick => (
        <TimelineTickStyled
          key={tick.key}
          $color={tick.color}
          $isStation={tick.isStation}
          $active={
            tick.stationIndex !== undefined
              ? tick.stationIndex === currentStationIndex
              : tick.eventId === activeEventId
          }
          $leftPercent={tick.fraction * PERCENT}
          title={tick.title}
        />
      ))}
      <TimelinePlayheadStyled ref={thumbRef} />
    </TimelineTrackStyled>
  );
};
